import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';
import ProductPreviewGallery from './ProductPreviewGallery';
import '../../styles/Prova.css';

const MOBILE_OFFER_QUERY = '(max-width: 600px)';
const ORIGINAL_PRICE = 49.90;

const isMobileOfferViewport = () => (
  typeof window !== 'undefined'
  && typeof window.matchMedia === 'function'
  && window.matchMedia(MOBILE_OFFER_QUERY).matches
);

export const formatBRL = (value) => Number(value).toLocaleString('pt-BR', {
  style: 'currency',
  currency: 'BRL',
});

const Benefit = ({ children }) => (
  <li>
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m5 12 4 4L19 6" />
    </svg>
    <span>{children}</span>
  </li>
);

const SecurityNote = () => (
  <p className="offer-card-security">
    <span aria-hidden="true">🔒</span>
    Pagamento seguro e único, sem assinatura. Você poderá revisar o pedido antes de pagar.
  </p>
);

const CertificateOffer = ({
  courseId,
  courseName,
  pricingOverride = null,
  resumeAccessOverride,
  onSelectOffer = null,
}) => {
  const [pricing, setPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasResumeAccess, setHasResumeAccess] = useState(
    typeof resumeAccessOverride === 'boolean' ? resumeAccessOverride : null,
  );
  const [accessLoading, setAccessLoading] = useState(
    typeof resumeAccessOverride !== 'boolean',
  );
  const [accessError, setAccessError] = useState('');
  const [navigating, setNavigating] = useState(false);
  const [isMobileViewport, setIsMobileViewport] = useState(isMobileOfferViewport);
  const externallyManaged = pricingOverride !== null;
  const accessExternallyManaged = typeof resumeAccessOverride === 'boolean';

  const loadPricing = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/config/payment`);
      const price = Number(
        response.data.professional_kit_price ?? response.data.certificate_price,
      );
      if (!Number.isFinite(price) || price <= 0) {
        throw new Error('Preço indisponível');
      }
      setPricing({
        price,
        cardEnabled: Boolean(response.data.card_enabled),
      });
    } catch (_) {
      setPricing(null);
      setError('Não foi possível carregar a oferta disponível.');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadResumeAccess = useCallback(async () => {
    try {
      setAccessLoading(true);
      setAccessError('');
      const token = localStorage.getItem('token');
      if (!token) throw new Error('Sessão não encontrada');
      const response = await axios.get(`${API_BASE_URL}/resumes/access`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHasResumeAccess(Boolean(response.data.has_access));
    } catch (_) {
      setHasResumeAccess(null);
      setAccessError('Não foi possível verificar os produtos disponíveis para sua conta.');
    } finally {
      setAccessLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!externallyManaged) loadPricing();
  }, [externallyManaged, loadPricing]);

  useEffect(() => {
    if (accessExternallyManaged) {
      setHasResumeAccess(resumeAccessOverride);
      setAccessLoading(false);
      setAccessError('');
      return;
    }
    loadResumeAccess();
  }, [accessExternallyManaged, loadResumeAccess, resumeAccessOverride]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;

    const mediaQuery = window.matchMedia(MOBILE_OFFER_QUERY);
    const updateViewport = () => setIsMobileViewport(mediaQuery.matches);
    updateViewport();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updateViewport);
      return () => mediaQuery.removeEventListener('change', updateViewport);
    }

    mediaQuery.addListener?.(updateViewport);
    return () => mediaQuery.removeListener?.(updateViewport);
  }, []);

  const visiblePricing = externallyManaged ? pricingOverride : pricing;
  const isLoading = (externallyManaged ? false : loading)
    || (accessExternallyManaged ? false : accessLoading);
  const visibleError = (externallyManaged ? '' : error) || accessError;
  const savings = visiblePricing
    ? Math.max(0, ORIGINAL_PRICE - Number(visiblePricing.price))
    : 0;

  const renderAction = () => {
    const label = hasResumeAccess ? 'Emitir Certificado' : 'Comprar o Kit Profissional';
    return onSelectOffer ? (
      <button type="button" className="offer-button offer-button-primary" onClick={onSelectOffer}>
        {label}
      </button>
    ) : (
      <Link
        to={`/cart/add?curso_id=${encodeURIComponent(String(courseId))}`}
        className="offer-button offer-button-primary"
        aria-busy={navigating}
        onClick={() => setNavigating(true)}
      >
        {navigating ? 'Abrindo pagamento...' : label}
      </Link>
    );
  };

  return (
    <>
      <section className="professional-offer" aria-labelledby="professional-offer-title">
      <header className="professional-offer-heading">
        <span className="professional-offer-eyebrow">Próximo passo</span>
        <h2 id="professional-offer-title">
          {hasResumeAccess
            ? 'Emita seu certificado e registre esta conquista'
            : 'Comprove sua nova qualificação e valorize seu currículo'}
        </h2>
        {hasResumeAccess ? (
          <p>
            Emita seu certificado digital verificável e comprove a conclusão do curso{' '}
            <strong>{courseName || 'concluído'}</strong>.
          </p>
        ) : (
          <p>
            Emita seu certificado digital verificável, comprove a conclusão do curso{' '}
            <strong>{courseName || 'concluído'}</strong> e fortaleça seu CURRÍCULO para novas
            oportunidades profissionais.
          </p>
        )}
      </header>

      {isLoading && (
        <div className="offer-loading" role="status">
          <span className="loading-spinner" aria-hidden="true" />
          Carregando oferta...
        </div>
      )}

      {visibleError && !isLoading && (
        <div className="offer-error" role="alert">
          <span>{visibleError}</span>
          <button
            type="button"
            onClick={() => {
              if (!externallyManaged) loadPricing();
              if (!accessExternallyManaged) loadResumeAccess();
            }}
          >
            Tentar novamente
          </button>
        </div>
      )}

      {visiblePricing && !isLoading && (
        <>
          <div className="offer-trust-strip" role="list" aria-label="Informações da compra">
            <span role="listitem">✓ Pagamento único</span>
            <span role="listitem">✓ Liberação após confirmação</span>
            <span role="listitem">✓ Certificado verificável online</span>
            <span role="listitem">✓ {visiblePricing.cardEnabled ? 'Pix ou cartão' : 'Pagamento por Pix'}</span>
          </div>

          <ProductPreviewGallery showResume={!hasResumeAccess} />

          <div className="offer-cards single-product">
            <article className="offer-card offer-card-featured">
              {!hasResumeAccess && (
                <div className="offer-recommended">PROMOÇÃO POR TEMPO LIMITADO</div>
              )}
              <div className="offer-card-main">
                <div className="offer-card-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M6 3h9l4 4v14H6V3Z" />
                    <path d="M15 3v5h4M9 12h7M9 16h7" />
                  </svg>
                </div>
                <h3>
                  {hasResumeAccess ? 'Emitir Certificado' : 'Kit Profissional: Certificado + Currículo'}
                </h3>
                <p>
                  {hasResumeAccess
                    ? 'Você já emitiu o kit profissional e tem direito vitalício a nossa ferramenta'
                    : 'Tenha seu Certificado verificável e um currículo profissional pronto para apresentar suas qualificações. Edite, atualize e baixe quantas vezes quiser.'}
                </p>
                <ul className="offer-benefits">
                  <Benefit>Certificado verificável em PDF</Benefit>
                  <Benefit>Código de autenticidade e validação online</Benefit>
                  <Benefit>Currículo com visual profissional</Benefit>
                  <Benefit>Curso concluído adicionado automaticamente</Benefit>
                  <Benefit>
                    {hasResumeAccess
                      ? 'Edição e downloads ilimitados do Currículo'
                      : 'Edição e downloads ilimitados'}
                  </Benefit>
                </ul>
              </div>
              <div className="offer-card-footer">
                <div className="offer-price-breakdown offer-single-price">
                  <span className={`total ${hasResumeAccess ? 'certificate-only' : 'professional-kit'}`}>
                    {!hasResumeAccess && (
                      <span className="offer-product-label">Certificado + Currículo</span>
                    )}
                    <span className="offer-promotional-price">
                      <span className="offer-original-price">
                        De <s className="offer-original-amount">R$ 49,90</s> POR
                      </span>
                      <strong className="offer-current-price">
                        {formatBRL(visiblePricing.price)}
                      </strong>
                      <span className="offer-savings">
                        Economize {formatBRL(savings)}
                      </span>
                    </span>
                  </span>
                </div>
                {!isMobileViewport && (
                  <div className="offer-inline-action">{renderAction()}</div>
                )}
                <SecurityNote />
              </div>
            </article>
          </div>
        </>
      )}
      </section>

      {visiblePricing && !isLoading && isMobileViewport && typeof document !== 'undefined'
        && createPortal(
          <div className="offer-mobile-fixed-action">
            {renderAction()}
          </div>,
          document.body,
        )}
    </>
  );
};

export default CertificateOffer;
